import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { ProgressBar, Spinner } from '@ntconsult/aluma-react';

interface UploadingFile {
  id: string;
  name: string;
  progress: number;
  status: 'uploading' | 'processing' | 'done' | 'error';
}

interface UploadProgressListProps {
  files: UploadingFile[];
}

export function UploadProgressList({ files }: UploadProgressListProps) {
  const { t } = useTranslation('files');

  return (
    <div className="mt-4 space-y-3">
      {files.map((file) => (
        <div key={file.id} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
          {file.status === 'uploading' && <Spinner size="sm" tone="brand" />}
          {file.status === 'processing' && (
            <span className="text-warning">
              <Spinner size="sm" tone="current" />
            </span>
          )}
          {file.status === 'done' && <CheckCircle className="w-5 h-5 text-success" />}
          {file.status === 'error' && <AlertCircle className="w-5 h-5 text-danger" />}

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{file.name}</p>
            {file.status === 'uploading' && (
              <ProgressBar
                className="mt-1"
                value={file.progress}
                size="xs"
                tone="brand"
                label={file.name}
              />
            )}
            {file.status === 'processing' && (
              <p className="text-xs text-warning">{t('processing')}</p>
            )}
            {file.status === 'done' && (
              <p className="text-xs text-success">{t('completed')}</p>
            )}
            {file.status === 'error' && (
              <p className="text-xs text-danger">{t('uploadError')}</p>
            )}
          </div>

          {file.status === 'uploading' && (
            <span className="text-sm text-foreground-muted">{file.progress}%</span>
          )}
        </div>
      ))}
    </div>
  );
}
